import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw, Flame, Dumbbell, Layers, Gauge, Zap, Timer } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";

const TIERS = {
  10: { name: "10-Min Ignition", desc: "All-out intervals, zero filler. Done before your coffee cools." },
  20: { name: "20-Min Builder", desc: "Supersets with trimmed rest — a full strength hit on a lunch break." },
  30: { name: "30-Min Burn", desc: "Strength-meets-conditioning. The session most members live on." },
  40: { name: "40-Min Grind", desc: "Extended work capacity for days you mean business." },
  50: { name: "50-Min Full Send", desc: "The complete session: warm-up, max volume, finisher, cooldown." },
};

const QUESTIONS = [
  {
    id: "minutes",
    q: "How many minutes do you actually have?",
    options: [
      { v: 10, l: "10", sub: "Espresso shot" },
      { v: 20, l: "20", sub: "Lunch break" },
      { v: 30, l: "30", sub: "The sweet spot" },
      { v: 40, l: "40", sub: "Serious work" },
      { v: 50, l: "50", sub: "Full send" },
    ],
  },
  {
    id: "goal",
    q: "What's today's mission?",
    options: [
      { v: "Burn fat", l: "Burn Fat", sub: "Heart rate up, sweat on", icon: Flame },
      { v: "Build strength", l: "Build Strength", sub: "Heavy, controlled, progressive", icon: Dumbbell },
      { v: "Full-body hybrid", l: "Hybrid", sub: "A bit of everything", icon: Layers },
    ],
  },
  {
    id: "intensity",
    q: "How hard do you want to go?",
    options: [
      { v: "Steady", l: "Steady", sub: "Move well, finish fresh", icon: Gauge },
      { v: "Spicy", l: "Spicy", sub: "Comfortably uncomfortable", icon: Zap },
      { v: "All-out", l: "All-Out", sub: "Leave nothing", icon: Timer },
    ],
  },
];

const scrollTo = (href) => {
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -72, duration: 1.4 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export const Quiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const done = step >= QUESTIONS.length;
  const tier = done ? TIERS[answers.minutes] : null;

  const answer = (id, v) => {
    setAnswers((a) => ({ ...a, [id]: v }));
    setStep((s) => s + 1);
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
  };

  return (
    <section id="quiz" data-testid="quiz-section" className="relative py-32 bg-carbon scroll-mt-20 overflow-hidden">
      <div className="absolute inset-0 hero-spotlight" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeUp className="text-center">
          <Eyebrow>Find Your Session</Eyebrow>
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl mt-4">
            Three questions. <span className="text-volt">Your perfect workout.</span>
          </h2>
        </FadeUp>

        <FadeUp delay={0.15}>
          <div className="mt-12 border border-white/10 bg-obsidian p-6 sm:p-10 min-h-[380px] flex flex-col">
            <div className="flex items-center gap-3 mb-8">
              {QUESTIONS.map((_, i) => (
                <div key={i} className="flex-1 h-1 bg-white/10 overflow-hidden">
                  <motion.div
                    animate={{ width: step > i ? "100%" : "0%" }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className="h-full bg-volt"
                  />
                </div>
              ))}
              <span className="font-mono text-[10px] tracking-widest uppercase text-neutral-500 ml-2">
                {done ? "Done" : `${step + 1}/3`}
              </span>
            </div>

            <AnimatePresence mode="wait">
              {!done ? (
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="flex-1 flex flex-col"
                >
                  <h3 className="font-display font-extrabold uppercase text-2xl sm:text-3xl leading-tight">
                    {QUESTIONS[step].q}
                  </h3>
                  <div className={`mt-8 grid gap-3 flex-1 ${QUESTIONS[step].id === "minutes" ? "grid-cols-2 sm:grid-cols-5" : "sm:grid-cols-3"}`}>
                    {QUESTIONS[step].options.map((o) => (
                      <button
                        key={o.l}
                        data-testid={`quiz-option-${QUESTIONS[step].id}-${String(o.v).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => answer(QUESTIONS[step].id, o.v)}
                        className="group border border-white/15 bg-carbon p-5 text-left hover:border-volt hover:bg-steel transition-colors duration-200 flex flex-col justify-between"
                      >
                        {o.icon && <o.icon className="h-6 w-6 text-volt mb-4" />}
                        <span className={`font-display font-black uppercase text-volt ${QUESTIONS[step].id === "minutes" ? "text-4xl" : "text-2xl"}`}>
                          {QUESTIONS[step].id === "minutes" ? o.l : o.l}
                        </span>
                        <span className="mt-1 text-xs text-neutral-500 group-hover:text-neutral-300 transition-colors">{o.sub}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="result"
                  data-testid="quiz-result"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="flex-1 flex flex-col items-center justify-center text-center"
                >
                  <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">Your Session</span>
                  <p className="font-display font-black uppercase text-5xl sm:text-6xl mt-4 leading-none">
                    {tier.name.split(" ").slice(0, -1).join(" ")}{" "}
                    <span className="text-volt italic">{tier.name.split(" ").slice(-1)}</span>
                  </p>
                  <p className="mt-5 max-w-md text-base text-neutral-400 leading-relaxed">
                    {tier.desc} Tuned for {answers.goal?.toLowerCase()} at {answers.intensity?.toLowerCase()} intensity.
                  </p>
                  <div className="mt-8 flex flex-wrap justify-center gap-3">
                    <button
                      data-testid="quiz-claim-access-button"
                      onClick={() => scrollTo("#access")}
                      className="group inline-flex items-center gap-3 bg-volt text-white font-display font-bold uppercase tracking-wide px-8 py-4 hover:bg-volt-hover hover:shadow-[0_0_35px_rgba(255,107,0,0.5)] transition-[background-color,box-shadow] duration-200"
                    >
                      Claim Founding Access
                      <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                    </button>
                    <button
                      data-testid="quiz-restart-button"
                      onClick={restart}
                      className="inline-flex items-center gap-2 border border-white/25 px-6 py-4 font-display font-bold uppercase text-neutral-200 hover:border-volt hover:text-volt transition-colors duration-200"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Retake
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
